import { NoSsr } from '@material-ui/core';
import cn from 'classnames';
import parse from 'html-react-parser';
import dynamic from 'next/dynamic';
import React from 'react';
import { useSelector } from 'react-redux';
import { ImageWithFallback } from '../../../components';
import moment from 'moment';
import styles from '../../../components/Article/Article.module.scss';
import { withLayout } from '../../../Layout/Layout';
import { httpGet } from '../../../utils/httpClient';
import Head from 'next/head';

const CallToSignUp = dynamic(() =>
  import('../../../components/CallToSignUp/CallToSignUp')
);

const SocialMediaLinks = dynamic(() =>
  import('../../../components/SocialMediaLinks/SocialMediaLinks')
);
const RecommendationsList = dynamic(() =>
  import('../../../components/RecommendationsList/RecommendationsList')
);

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};
export const getStaticProps = async ({ params }) => {
  const article = await getArchiveArticle(params);
  if (article.error) {
    return {
      notFound: true,
      revalidate: 60 * 60 * 2,
    };
  }
  return {
    props: {
      article: article,
      recommendation: await getLatestRecommendation(),
    },
    revalidate: 60 * 60 * 2,
  };
};

const getArchiveArticle = async (params) => {
  return (
    await httpGet(
      `${process.env.apiUrl}/api/archive/article/${params.all[0]}/${params.all[1]}`
    )
  ).json();
};
const getLatestRecommendation = async () => {
  return (await httpGet(`${process.env.javaApiUrl}/recommendation`)).json();
};

function ArchiveArticle({ article, recommendation, authCompleted }) {
  const userStore = useSelector((state) => state.user.value);
  const root = parse(article.content, {
    replace: (domNode) => {
      // generation link, removing everything before /wp-content
      if (domNode.type === 'tag' && domNode.name === 'img') {
        return domNode.parent ? (
          <div>
            <div className={styles.imageInline}>
              <ImageWithFallback
                src={
                  process.env.mediaUrl +
                  domNode.attribs.src.substring(
                    domNode.attribs.src.lastIndexOf('/wp-content') + 1
                  )
                }
                layout="fill"
                objectFit="cover"
              />
            </div>
          </div>
        ) : (
          <div className={styles.imageInline}>
            <ImageWithFallback
              src={
                process.env.mediaUrl +
                domNode.attribs.src.substring(
                  domNode.attribs.src.lastIndexOf('/wp-content') + 1
                )
              }
              layout="fill"
              objectFit="cover"
            />
          </div>
        );
      }
      if (
        domNode.name === 'p' &&
        domNode.children[0] &&
        domNode.children[0].name === 'img'
      ) {
        return (
          <div className={styles.imageInline}>
            <ImageWithFallback
              src={
                process.env.mediaUrl +
                domNode.children[0].attribs.src.substring(
                  domNode.children[0].attribs.src.lastIndexOf('/wp-content') + 1
                )
              }
              layout="fill"
              objectFit="cover"
            />
          </div>
        );
      }
      // [caption] replacement
      if (domNode.type === 'text') {
        if (
          (domNode.parent && domNode.parent.name === 'p') ||
          (domNode.parent &&
            domNode.parent.parent &&
            domNode.parent.parent.name === 'p')
        ) {
          return;
        }
        domNode.data = domNode.data.replace(new RegExp(/\[.+\]/g), '');
        return domNode.parent ? (
          <p
            className="text-justify"
            dangerouslySetInnerHTML={{
              __html: `<${domNode.parent.name}>${domNode.data}</${domNode.parent.name}>`,
            }}
          />
        ) : (
          <p className="text-justify">{domNode.data}</p>
        );
      }
    },
  });
  let metaDescription = '';
  const parseTextFromNode = (node) => {
    if (typeof node !== 'string') {
      if (node && Array.isArray(node.props.children)) {
        node.props.children.forEach((child) => {
          parseTextFromNode(child);
        });
      } else if (node) {
        parseTextFromNode(node.props.children);
      }
    } else {
      metaDescription += node;
    }
  };
  parseTextFromNode(root[0]);
  return (
    <>
      <Head>
        <title>{article.title} - First-Class.com</title>
        <meta name="description" content={metaDescription} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="first-class.com" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta
          name="twitter:image"
          content={process.env.mediaUrl + article.image}
        />
        <meta property="og:title" content={article.title} />
        <meta property="og:type" content="article" />
        <meta
          property="og:image"
          content={process.env.mediaUrl + article.image}
        />
        <meta property="og:description" content={metaDescription} />
      </Head>
      <div className={cn('container font-libre')}>
        <div className={styles.article}>
          <div className={styles.overline}>{article.overline}</div>
          <h1
            className={cn(styles.title, 'font-fragment')}
            dangerouslySetInnerHTML={{ __html: article.title }}
          />
          <div className="text-center">
            {moment(article.published).format('D. MMM yyyy')}
          </div>
          <div className="text-center mb-7">by {article.author}</div>
          <div className={styles.mainImage}>
            <ImageWithFallback
              src={
                process.env.mediaUrl +
                article.image?.substring(
                  article.image?.lastIndexOf('/wp-content') + 1
                )
              }
              layout="fill"
              objectFit="cover"
            />
          </div>
          <div className="content font-libre mt-4">{root}</div>
          <NoSsr>
            {typeof document !== 'undefined' && <SocialMediaLinks />}
          </NoSsr>
        </div>
      </div>
      {userStore && !userStore.userid && (
        <div className="w-full mx-auto my-20 sm:mb-10">
          <CallToSignUp
            country={userStore?.country}
            authCompleted={authCompleted}
            sourcePage={'Archive article'}
          />
        </div>
      )}
      <div className="container flex justify-center items-center sm:w-full">
        <RecommendationsList items={recommendation} />
      </div>
    </>
  );
}

export default withLayout(ArchiveArticle, false);
